import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type {
  HelpAssistantReply,
  HelpChatRequest,
  HelpChatResponse,
  HelpEscalationResponse,
  HelpFeedbackResponse,
  HelpMessage,
  HelpPageContext,
  SupportedLocale,
} from '@accestra/types';
import { api, friendlyError } from '../../lib/api-client';
import { normalizeLocale } from '../../i18n/localeTypes';
import { getShellPageContext } from '../../lib/app-page-context';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import AlertBanner from '../ui/AlertBanner';

interface HelpCopilotDrawerProps {
  open: boolean;
  onClose: () => void;
}

interface FeedbackState {
  rating: 'helpful' | 'not_helpful';
  pending: boolean;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeOptionalString(value: string | null | undefined): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeOptionalUuid(value: string | null | undefined): string | null {
  const trimmed = normalizeOptionalString(value);
  if (!trimmed || !UUID_PATTERN.test(trimmed)) {
    return null;
  }

  return trimmed;
}

function sanitizeHelpPageContext(pageContext: HelpPageContext): HelpPageContext {
  const sanitized: HelpPageContext = {
    routePath: pageContext.routePath.trim(),
  };

  const routeLabel = normalizeOptionalString(pageContext.routeLabel);
  if (routeLabel) {
    sanitized.routeLabel = routeLabel;
  }

  const pageKey = normalizeOptionalString(pageContext.pageKey);
  if (pageKey) {
    sanitized.pageKey = pageKey;
  }

  const siteId = normalizeOptionalUuid(pageContext.siteId);
  if (siteId) {
    sanitized.siteId = siteId;
  }

  const scanId = normalizeOptionalUuid(pageContext.scanId);
  if (scanId) {
    sanitized.scanId = scanId;
  }

  const reportId = normalizeOptionalString(pageContext.reportId);
  if (reportId) {
    sanitized.reportId = reportId;
  }

  return sanitized;
}

function buildHelpChatRequest(input: {
  conversationId: string | null;
  message: string;
  language: SupportedLocale;
  pageContext: HelpPageContext;
}): HelpChatRequest {
  const request: HelpChatRequest = {
    message: input.message.trim(),
    language: input.language,
    pageContext: sanitizeHelpPageContext(input.pageContext),
  };

  if (input.conversationId) {
    request.conversationId = input.conversationId;
  }

  return request;
}

function getAssistantReply(message: HelpMessage): HelpAssistantReply | null {
  if (!isRecord(message.metadata)) {
    return null;
  }

  const candidate = message.metadata['assistantReply'];
  if (!isRecord(candidate)) {
    return null;
  }

  if (
    typeof candidate['answer'] !== 'string'
    || typeof candidate['explanation'] !== 'string'
    || !Array.isArray(candidate['recommendedNextSteps'])
    || !Array.isArray(candidate['relatedTopics'])
    || (candidate['confidence'] !== 'high' && candidate['confidence'] !== 'medium' && candidate['confidence'] !== 'low')
    || typeof candidate['escalateSuggested'] !== 'boolean'
    || !Array.isArray(candidate['sourceRefs'])
  ) {
    return null;
  }

  return candidate as unknown as HelpAssistantReply;
}

function getMessageCategory(message: HelpMessage): string | null {
  if (!isRecord(message.metadata)) {
    return null;
  }

  return typeof message.metadata['category'] === 'string'
    ? message.metadata['category']
    : null;
}

function confidenceVariant(confidence: HelpAssistantReply['confidence']): 'success' | 'warning' | 'danger' {
  switch (confidence) {
    case 'high':
      return 'success';
    case 'medium':
      return 'warning';
    case 'low':
    default:
      return 'danger';
  }
}

function getLatestUserQuestion(messages: HelpMessage[], assistantMessageId: string): string {
  const assistantIndex = messages.findIndex((message) => message.id === assistantMessageId);
  if (assistantIndex <= 0) {
    return '';
  }

  for (let index = assistantIndex - 1; index >= 0; index -= 1) {
    if (messages[index]?.role === 'user') {
      return messages[index]?.content ?? '';
    }
  }

  return '';
}

export default function HelpCopilotDrawer({
  open,
  onClose,
}: HelpCopilotDrawerProps) {
  const { t, i18n } = useTranslation('help');
  const { t: tCommon } = useTranslation('common');
  const location = useLocation();
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<HelpMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const [feedbackStates, setFeedbackStates] = useState<Record<string, FeedbackState>>({});
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [escalationState, setEscalationState] = useState<{ tone: 'success' | 'warning' | 'danger'; message: string } | null>(null);
  const [escalatingMessageId, setEscalatingMessageId] = useState<string | null>(null);
  const [conversationViewNonce, setConversationViewNonce] = useState(0);
  const endRef = useRef<HTMLDivElement | null>(null);
  const requestGenerationRef = useRef(0);

  const currentLocale = normalizeLocale(i18n.resolvedLanguage ?? i18n.language) as SupportedLocale;
  const pageContext = useMemo<HelpPageContext>(
    () => {
      const context = getShellPageContext(location.pathname, location.search, tCommon);
      return sanitizeHelpPageContext({
        routePath: context.routePath,
        routeLabel: context.label,
        pageKey: context.pageKey,
        siteId: context.siteId ?? null,
        scanId: context.scanId ?? null,
        reportId: context.reportId ?? null,
      });
    },
    [location.pathname, location.search, tCommon],
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose, open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const timer = window.setTimeout(() => {
      endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 60);

    return () => window.clearTimeout(timer);
  }, [messages, open, isSending]);

  const conversationViewKey = conversationId ?? `draft-${conversationViewNonce}`;

  const mergeConversationMessages = (
    currentMessages: HelpMessage[],
    nextMessages: HelpMessage[],
  ): HelpMessage[] => {
    const merged = [...currentMessages];

    for (const nextMessage of nextMessages) {
      const existingIndex = merged.findIndex((message) => message.id === nextMessage.id);
      if (existingIndex >= 0) {
        merged[existingIndex] = nextMessage;
      } else {
        merged.push(nextMessage);
      }
    }

    return merged;
  };

  const submitQuestion = async (questionText: string) => {
    const trimmed = questionText.trim();
    if (!trimmed || isSending) {
      return;
    }

    const activeConversationId = conversationId;
    const generation = requestGenerationRef.current;
    setIsSending(true);
    setChatError(null);
    setEscalationState(null);

    try {
      const response = await api.post<HelpChatResponse>(
        '/help/chat',
        buildHelpChatRequest({
          conversationId: activeConversationId,
          message: trimmed,
          language: currentLocale,
          pageContext,
        }),
      );

      if (requestGenerationRef.current !== generation) {
        return;
      }

      setInputValue('');
      setConversationId(response.conversation.id);
      setMessages((currentMessages) => (
        activeConversationId && activeConversationId === response.conversation.id
          ? mergeConversationMessages(currentMessages, [response.userMessage, response.assistantMessage])
          : [response.userMessage, response.assistantMessage]
      ));
    } catch (error) {
      if (requestGenerationRef.current === generation) {
        setChatError(friendlyError(error, t('states.chatError')));
      }
    } finally {
      if (requestGenerationRef.current === generation) {
        setIsSending(false);
      }
    }
  };

  const handleFeedback = async (
    messageId: string,
    rating: 'helpful' | 'not_helpful',
  ) => {
    setFeedbackError(null);
    setFeedbackStates((current) => ({
      ...current,
      [messageId]: { rating, pending: true },
    }));

    try {
      await api.post<HelpFeedbackResponse>('/help/feedback', {
        messageId,
        rating,
        resolved: rating === 'helpful',
      });
      setFeedbackStates((current) => ({
        ...current,
        [messageId]: { rating, pending: false },
      }));
    } catch (error) {
      setFeedbackStates((current) => ({
        ...current,
        [messageId]: { rating, pending: false },
      }));
      setFeedbackError(friendlyError(error, t('states.feedbackError')));
    }
  };

  const handleEscalate = async (assistantMessage: HelpMessage) => {
    if (escalatingMessageId) {
      return;
    }

    const userQuestion = getLatestUserQuestion(messages, assistantMessage.id);
    setEscalationState(null);
    setEscalatingMessageId(assistantMessage.id);

    try {
      const response = await api.post<HelpEscalationResponse>('/help/escalate', {
        conversationId,
        siteId: pageContext.siteId ?? null,
        scanId: pageContext.scanId ?? null,
        reportId: pageContext.reportId ?? null,
        language: currentLocale,
        routeContext: pageContext.routePath,
        pageContext,
        userQuestion: userQuestion || t('escalation.fallbackQuestion'),
        aiSummary: assistantMessage.content,
        category: getMessageCategory(assistantMessage),
      });

      if (response.emailDeliveryStatus === 'sent') {
        setEscalationState({ tone: 'success', message: t('escalation.status.sent') });
      } else if (response.emailDeliveryStatus === 'failed') {
        setEscalationState({ tone: 'warning', message: t('escalation.status.failed') });
      } else {
        setEscalationState({ tone: 'success', message: t('escalation.status.saved') });
      }
    } catch (error) {
      setEscalationState({
        tone: 'danger',
        message: friendlyError(error, t('escalation.status.error')),
      });
    } finally {
      setEscalatingMessageId(null);
    }
  };

  const resetConversation = () => {
    requestGenerationRef.current += 1;
    setConversationViewNonce((current) => current + 1);
    setConversationId(null);
    setMessages([]);
    setInputValue('');
    setIsSending(false);
    setChatError(null);
    setFeedbackError(null);
    setEscalationState(null);
    setEscalatingMessageId(null);
    setFeedbackStates({});
  };

  return (
    <div className={open ? 'acc-help-drawer acc-help-drawer--open' : 'acc-help-drawer'} aria-hidden={!open}>
      <button
        type="button"
        className="acc-help-drawer__backdrop"
        aria-label={t('drawer.close')}
        onClick={onClose}
      />

      <aside className="acc-help-drawer__panel" aria-label={t('drawer.title')}>
        <header className="acc-help-drawer__header">
          <div>
            <div className="acc-help-drawer__eyebrow">{t('drawer.eyebrow')}</div>
            <h2 className="acc-help-drawer__title">{t('drawer.title')}</h2>
            <p className="acc-help-drawer__subtitle">{t('drawer.subtitle')}</p>
          </div>

          <div className="acc-help-drawer__header-actions">
            {messages.length > 0 && (
              <Button variant="ghost" size="sm" onClick={resetConversation}>
                {t('drawer.newConversation')}
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose} aria-label={t('drawer.close')}>
              {t('drawer.closeButton')}
            </Button>
          </div>
        </header>

        {escalationState && (
          <div className="acc-help-drawer__banner">
            <AlertBanner variant={escalationState.tone === 'danger' ? 'danger' : escalationState.tone === 'warning' ? 'warning' : 'success'}>
              {escalationState.message}
            </AlertBanner>
          </div>
        )}

        {chatError && (
          <div className="acc-help-drawer__banner">
            <AlertBanner variant="danger">{chatError}</AlertBanner>
          </div>
        )}

        {feedbackError && (
          <div className="acc-help-drawer__banner">
            <AlertBanner variant="warning">{feedbackError}</AlertBanner>
          </div>
        )}

        <div key={conversationViewKey} className="acc-help-drawer__body">
          {messages.length === 0 ? (
            <section className="acc-help-drawer__empty">
              <Badge variant="info">{t('intro.badge')}</Badge>
              <h3 className="acc-help-drawer__empty-title">{t('intro.title')}</h3>
              <p className="acc-help-drawer__empty-body">{t('intro.body')}</p>

              <div className="acc-help-drawer__samples">
                {[
                  t('samples.runScans'),
                  t('samples.authenticated'),
                  t('samples.severity'),
                  t('samples.billing'),
                ].map((sample) => (
                  <button
                    key={sample}
                    type="button"
                    className="acc-help-drawer__sample"
                    onClick={() => void submitQuestion(sample)}
                  >
                    {sample}
                  </button>
                ))}
              </div>
            </section>
          ) : (
            <div className="acc-help-thread">
              {messages.map((message) => {
                const assistantReply = message.role === 'assistant' ? getAssistantReply(message) : null;
                const feedbackState = feedbackStates[message.id];

                return (
                  <article
                    key={message.id}
                    className={
                      message.role === 'user'
                        ? 'acc-help-message acc-help-message--user'
                        : 'acc-help-message acc-help-message--assistant'
                    }
                  >
                    <div className="acc-help-message__meta">
                      <span className="acc-help-message__author">
                        {message.role === 'user' ? t('message.you') : t('message.assistant')}
                      </span>
                      {assistantReply && (
                        <Badge variant={confidenceVariant(assistantReply.confidence)}>
                          {t(`message.confidence.${assistantReply.confidence}`)}
                        </Badge>
                      )}
                    </div>

                    {message.role === 'assistant' && assistantReply ? (
                      <div className="acc-help-message__assistant">
                        <p className="acc-help-message__answer">{assistantReply.answer}</p>

                        <div className="acc-help-message__section">
                          <div className="acc-help-message__section-label">{t('message.explanation')}</div>
                          <p className="acc-help-message__paragraph">{assistantReply.explanation}</p>
                        </div>

                        {assistantReply.recommendedNextSteps.length > 0 && (
                          <div className="acc-help-message__section">
                            <div className="acc-help-message__section-label">{t('message.nextSteps')}</div>
                            <ul className="acc-help-message__list">
                              {assistantReply.recommendedNextSteps.map((step: HelpAssistantReply['recommendedNextSteps'][number]) => (
                                <li key={step}>{step}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {assistantReply.sourceRefs.length > 0 && (
                          <div className="acc-help-message__section">
                            <div className="acc-help-message__section-label">{t('message.sources')}</div>
                            <div className="acc-help-message__chips">
                              {assistantReply.sourceRefs.map((source: HelpAssistantReply['sourceRefs'][number]) => (
                                source.routePath ? (
                                  <Link key={source.id} to={source.routePath} className="acc-help-chip">
                                    {source.title}
                                  </Link>
                                ) : (
                                  <span key={source.id} className="acc-help-chip">
                                    {source.title}
                                  </span>
                                )
                              ))}
                            </div>
                          </div>
                        )}

                        {assistantReply.relatedTopics.length > 0 && (
                          <div className="acc-help-message__section">
                            <div className="acc-help-message__section-label">{t('message.relatedTopics')}</div>
                            <div className="acc-help-message__chips">
                              {assistantReply.relatedTopics.map((topic: HelpAssistantReply['relatedTopics'][number]) => (
                                <span key={topic} className="acc-help-chip acc-help-chip--muted">
                                  {topic}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="acc-help-message__actions">
                          <span className="acc-help-message__feedback-label">{t('message.feedbackPrompt')}</span>
                          <Button
                            variant={feedbackState?.rating === 'helpful' ? 'primary' : 'secondary'}
                            size="sm"
                            disabled={feedbackState?.pending}
                            onClick={() => void handleFeedback(message.id, 'helpful')}
                          >
                            {feedbackState?.pending && feedbackState.rating === 'helpful'
                              ? t('message.feedbackSaving')
                              : t('message.helpful')}
                          </Button>
                          <Button
                            variant={feedbackState?.rating === 'not_helpful' ? 'primary' : 'secondary'}
                            size="sm"
                            disabled={feedbackState?.pending}
                            onClick={() => void handleFeedback(message.id, 'not_helpful')}
                          >
                            {feedbackState?.pending && feedbackState.rating === 'not_helpful'
                              ? t('message.feedbackSaving')
                              : t('message.notHelpful')}
                          </Button>
                        </div>

                        {assistantReply.escalateSuggested && (
                          <div className="acc-help-message__escalation">
                            <AlertBanner variant="warning">
                              {t('escalation.prompt')}
                            </AlertBanner>
                            <div className="acc-help-message__escalation-actions">
                              <Button
                                size="sm"
                                onClick={() => void handleEscalate(message)}
                                disabled={escalatingMessageId === message.id}
                              >
                                {escalatingMessageId === message.id
                                  ? t('escalation.sending')
                                  : t('escalation.button')}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="acc-help-message__paragraph">{message.content}</p>
                    )}
                  </article>
                );
              })}
            </div>
          )}

          {isSending && (
            <div className="acc-help-loading">
              <Badge variant="info">{t('composer.sending')}</Badge>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <footer className="acc-help-drawer__composer">
          <label className="acc-help-drawer__composer-label" htmlFor="acc-help-composer">
            {t('composer.label')}
          </label>
          <textarea
            id="acc-help-composer"
            className="acc-help-drawer__textarea"
            rows={4}
            value={inputValue}
            placeholder={t('composer.placeholder')}
            onChange={(event) => setInputValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                void submitQuestion(inputValue);
              }
            }}
          />
          <div className="acc-help-drawer__composer-footer">
            <div className="acc-help-drawer__composer-note">
              {t('composer.note', { route: pageContext.routeLabel ?? pageContext.routePath })}
            </div>
            <Button onClick={() => void submitQuestion(inputValue)} disabled={isSending || inputValue.trim().length === 0}>
              {isSending ? t('composer.sending') : t('composer.send')}
            </Button>
          </div>
        </footer>
      </aside>
    </div>
  );
}
